import React, { useEffect, useRef, useState } from 'react';
import {
  Card, Button, Space, message, Form, Input, Switch, Upload, Typography, Image, Tag, Tabs, DatePicker, Modal, Alert,
} from 'antd';
import { ArrowLeftOutlined, UploadOutlined, SaveOutlined, EyeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { apiClient } from 'Utils/api';
import { useIsMobile } from 'Utils/useIsMobile';
import { useLanguage } from '../../Context/LanguageContext';
import { GIVEAWAY_STATUS } from './GiveawaysPage';
import GiveawayEntriesTab from '../Components/GiveawayEntries/GiveawayEntriesTab';

const { Title, Text } = Typography;
const { TextArea } = Input;

const apiBase = process.env.REACT_APP_API_URL || '';
const FE_URL = process.env.REACT_APP_FE_URL || 'https://www.mr-luxury-watches.com';
const resolveImg = (url: string) => /^https?:\/\//i.test(url) ? url : `${apiBase}${url}`;

const DEFAULT_LANG = 'de';
const TRANSLATABLE_FIELDS = [ 'headline', 'teaser', 'prizeDescription', 'terms', 'ctaLabel', 'privacyNotice' ] as const;
type TranslatableField = typeof TRANSLATABLE_FIELDS[number];
type LangData = Record<TranslatableField, Record<string, string>>;

const EMPTY_LANG: LangData = { headline: {}, teaser: {}, prizeDescription: {}, terms: {}, ctaLabel: {}, privacyNotice: {} };

interface FormValues extends Record<TranslatableField, string> {
  title: string;
  slug: string;
  startsAt: Dayjs | null;
  endsAt: Dayjs | null;
  isActive: boolean;
  imageUrl: string;
}

const GiveawayEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = !id || id === 'new';
  const isMobile = useIsMobile();
  const [form] = Form.useForm<FormValues>();
  const [modal, modalContext] = Modal.useModal();
  const { currentLanguage } = useLanguage();
  const prevLangRef = useRef<string>(currentLanguage);

  const [giveaway, setGiveaway] = useState<any>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [langData, setLangData] = useState<LangData>(EMPTY_LANG);
  const previewImage = Form.useWatch('imageUrl', form);
  const activeTab = searchParams.get('tab') || 'general';

  const fillForm = (g: any) => {
    const initial = TRANSLATABLE_FIELDS.reduce((acc, f) => ({ ...acc, [f]: g[f] || {} }), {} as LangData);
    setLangData(initial);
    form.setFieldsValue({
      title: g.title,
      slug: g.slug,
      startsAt: g.startsAt ? dayjs(g.startsAt) : null,
      endsAt: g.endsAt ? dayjs(g.endsAt) : null,
      isActive: g.isActive,
      imageUrl: g.imageUrl || '',
      ...TRANSLATABLE_FIELDS.reduce((acc, f) => ({ ...acc, [f]: initial[f][currentLanguage] || '' }), {}),
    });
    prevLangRef.current = currentLanguage;
  };

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({ isActive: false, imageUrl: '' });
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(`/api/admin/giveaways/${id}`);
        if (res.data.status !== 'success') {
          message.error(res.data.message || 'Gewinnspiel nicht gefunden');
          navigate('/giveaways');
          return;
        }
        setGiveaway(res.data.giveaway);
        fillForm(res.data.giveaway);
      } catch (e: any) {
        message.error(e.response?.data?.message || 'Fehler beim Laden');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Keep the values of the previous language when the global language switcher changes
  useEffect(() => {
    const prevLang = prevLangRef.current;
    if (prevLang === currentLanguage) return;
    const current = form.getFieldsValue();
    setLangData((prev) => {
      const next = TRANSLATABLE_FIELDS.reduce((acc, f) => ({ ...acc, [f]: { ...prev[f], [prevLang]: (current as any)[f] || '' } }), {} as LangData);
      form.setFieldsValue(TRANSLATABLE_FIELDS.reduce((acc, f) => ({ ...acc, [f]: next[f][currentLanguage] || '' }), {}) as any);
      return next;
    });
    prevLangRef.current = currentLanguage;
  }, [currentLanguage, form]);

  const handleFormChange = (changed: Partial<FormValues>) => {
    const updates: Partial<LangData> = {};
    TRANSLATABLE_FIELDS.forEach((f) => {
      if (changed[f] !== undefined) updates[f] = { ...langData[f], [currentLanguage]: (changed[f] as string) || '' };
    });
    if (Object.keys(updates).length) setLangData((prev) => ({ ...prev, ...updates }));
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('images', file);
      const res = await apiClient.post('/api/admin/giveaways/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data?.imageUrl) {
        form.setFieldsValue({ imageUrl: res.data.imageUrl });
        message.success('Bild hochgeladen');
      } else {
        message.error(res.data?.message || 'Upload fehlgeschlagen');
      }
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    try {
      const v = await form.validateFields();
      const current = form.getFieldsValue();
      const finalLang = TRANSLATABLE_FIELDS.reduce((acc, f) => ({ ...acc, [f]: { ...langData[f], [currentLanguage]: (current as any)[f] || '' } }), {} as LangData);
      setSaving(true);
      const payload = {
        title: v.title,
        slug: v.slug,
        startsAt: v.startsAt?.toISOString(),
        endsAt: v.endsAt?.toISOString(),
        isActive: v.isActive,
        imageUrl: v.imageUrl || null,
        ...finalLang,
      };
      const res = isNew
        ? await apiClient.post('/api/admin/giveaways', payload)
        : await apiClient.put(`/api/admin/giveaways/${id}`, payload);
      if (res.data.status !== 'success') {
        message.error(res.data.message || 'Speichern fehlgeschlagen');
        if (res.data.code?.startsWith('SLUG')) setSearchParams({ tab: 'general' });
        return;
      }
      message.success(isNew ? 'Gewinnspiel angelegt' : 'Gewinnspiel gespeichert');
      setGiveaway(res.data.giveaway);
      setLangData(finalLang);
      if (isNew) navigate(`/giveaways/${res.data.giveaway.id}`, { replace: true });
    } catch (e: any) {
      if (e.errorFields?.length) {
        // Jump to the tab of the first invalid field, it may be hidden on another tab
        const field = String(e.errorFields[0].name[0]);
        setSearchParams({ tab: (TRANSLATABLE_FIELDS as readonly string[]).includes(field) ? 'texts' : 'general' }, { replace: true });
        message.error('Bitte die markierten Pflichtfelder ausfüllen');
      } else {
        message.error(e.response?.data?.message || 'Speichern fehlgeschlagen');
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = () => {
    modal.confirm({
      title: 'Gewinnspiel löschen',
      content: `Soll „${giveaway?.title}“ wirklich gelöscht werden? Die Zusatzinhalte werden mitgelöscht.`,
      okText: 'Ja, löschen',
      cancelText: 'Abbrechen',
      okButtonProps: { danger: true },
      onOk: async () => {
        const res = await apiClient.delete(`/api/admin/giveaways/${id}`);
        if (res.data.status !== 'success') {
          message.error(res.data.message || 'Löschen fehlgeschlagen');
          return;
        }
        message.success('Gewinnspiel gelöscht');
        navigate('/giveaways');
      },
    });
  };

  if (loading) return <div>Lade Gewinnspiel…</div>;

  const isDefault = currentLanguage === DEFAULT_LANG;
  const langSuffix = isDefault ? '' : ` (${currentLanguage.toUpperCase()})`;
  const placeholderFor = (field: TranslatableField, fallback: string) => (isDefault ? fallback : (langData[field]?.de || 'Deutsche Übersetzung'));
  const status = giveaway ? GIVEAWAY_STATUS[giveaway.status] : null;
  const previewUrl = giveaway ? `${FE_URL}/gewinnspiel/${giveaway.slug}?preview=${giveaway.previewKey}` : '';

  // Native pickers are easier to use on phones; desktop keeps the antd picker
  const dateField = (name: 'startsAt' | 'endsAt', label: string) => (
    <Form.Item
      label={label}
      name={name}
      rules={[ { required: true, message: `Bitte ${label} angeben` } ]}
      getValueProps={isMobile ? (value: Dayjs | null) => ({ value: value ? value.format('YYYY-MM-DDTHH:mm') : '' }) : undefined}
      getValueFromEvent={isMobile ? (e: any) => (e.target.value ? dayjs(e.target.value) : null) : undefined}
    >
      {isMobile
        ? <Input type="datetime-local" data-testid={`giveaway-${name}`} />
        : <DatePicker showTime={{ format: 'HH:mm' }} format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} data-testid={`giveaway-${name}`} />}
    </Form.Item>
  );

  const generalTab = (
    <Card>
      <Form.Item label="Titel (intern)" name="title" rules={[ { required: true, message: 'Bitte einen Titel angeben' } ]}>
        <Input placeholder="z. B. Weihnachtsgewinnspiel 2026" data-testid="giveaway-title" />
      </Form.Item>
      <Form.Item
        label="Slug"
        name="slug"
        extra={giveaway?.slugLocked
          ? 'Der Slug ist gesperrt, weil es bereits Teilnahmen gibt.'
          : 'Bildet die Adresse /gewinnspiel/<slug> und die Quelle der Newsletter-Anmeldungen. Nur Kleinbuchstaben, Ziffern und Bindestriche.'}
        rules={[
          { required: true, message: 'Bitte einen Slug angeben' },
          { pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/, message: 'Nur Kleinbuchstaben, Ziffern und Bindestriche, z. B. weihnachten-2026' },
        ]}
      >
        <Input placeholder="weihnachten-2026" disabled={!!giveaway?.slugLocked} data-testid="giveaway-slug" />
      </Form.Item>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 220px' }}>{dateField('startsAt', 'Start')}</div>
        <div style={{ flex: '1 1 220px' }}>{dateField('endsAt', 'Ende')}</div>
      </div>
      <Form.Item label="Aktiv" name="isActive" valuePropName="checked" extra="Nur aktive Gewinnspiele erscheinen im Shop. Aktive Zeiträume dürfen sich nicht überschneiden.">
        <Switch data-testid="giveaway-active" />
      </Form.Item>
      <Form.Item label="Titelbild (Pop-up und Seite)" style={{ marginBottom: 0 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => { handleUpload(file as File); return false; }}>
            <Button icon={<UploadOutlined />} loading={uploading}>Bild hochladen</Button>
          </Upload>
          <Form.Item name="imageUrl" noStyle>
            <Input placeholder="oder externe URL einfügen (https://...)" />
          </Form.Item>
          {previewImage && <Image src={resolveImg(previewImage)} alt="Vorschau" style={{ maxWidth: 360, width: '100%', borderRadius: 4 }} />}
        </Space>
      </Form.Item>
    </Card>
  );

  const textsTab = (
    <Card
      title={`Texte${langSuffix}`}
      extra={<Tag color={isDefault ? 'blue' : 'gold'}>Sprache: {currentLanguage.toUpperCase()}{isDefault ? ' (Standard)' : ''}</Tag>}
    >
      {!isDefault && <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Die Sprache wechselst du oben im Admin. Leere Felder zeigen im Shop den deutschen Text." />}
      <Form.Item label="Überschrift" name="headline" rules={isDefault ? [ { required: true, message: 'Bitte eine Überschrift angeben' } ] : []}>
        <Input placeholder={placeholderFor('headline', 'z. B. Gewinne eine von 10 MoonSwatches')} data-testid="giveaway-headline" />
      </Form.Item>
      <Form.Item label="Teaser (Pop-up und Seite)" name="teaser" rules={isDefault ? [ { required: true, message: 'Bitte einen Teaser angeben' } ] : []}>
        <TextArea rows={3} placeholder={placeholderFor('teaser', 'Kurzer Text, der Lust auf die Teilnahme macht')} />
      </Form.Item>
      <Form.Item label="Preisbeschreibung" name="prizeDescription">
        <TextArea rows={3} placeholder={placeholderFor('prizeDescription', 'Was gibt es zu gewinnen?')} />
      </Form.Item>
      <Form.Item label="Button-Text" name="ctaLabel">
        <Input placeholder={placeholderFor('ctaLabel', 'Jetzt teilnehmen')} />
      </Form.Item>
      <Form.Item label="Teilnahmebedingungen" name="terms" rules={isDefault ? [ { required: true, message: 'Bitte Teilnahmebedingungen angeben' } ] : []}>
        <TextArea rows={8} placeholder={placeholderFor('terms', 'Veranstalter, Zeitraum, Teilnahmeberechtigung, Ermittlung der Gewinner …')} />
      </Form.Item>
      <Form.Item label="Datenschutzhinweis" name="privacyNotice" extra="Mit dem Standardtext vorbelegt, bitte vor dem Start prüfen.">
        <TextArea rows={6} placeholder={placeholderFor('privacyNotice', 'Datenschutzhinweis')} />
      </Form.Item>
    </Card>
  );

  const contentTab = (
    <Card>
      <Space direction="vertical">
        <Text>Zusatzinhalte wie Bilder der Preise, Ablauf oder FAQ pflegst du mit dem Seiten-Editor. Sie erscheinen auf der Gewinnspiel-Seite zwischen Preisbeschreibung und Formular.</Text>
        <Button
          icon={<EditOutlined />}
          onClick={() => navigate(`/pages/${giveaway.contentSlug}`, { state: { backTo: `/giveaways/${id}?tab=content` } })}
          data-testid="giveaway-edit-content"
        >
          Zusatzinhalte bearbeiten
        </Button>
      </Space>
    </Card>
  );

  const tabs = [
    { key: 'general', label: 'Allgemein', children: generalTab, forceRender: true },
    { key: 'texts', label: 'Texte', children: textsTab, forceRender: true },
    ...(!isNew && giveaway ? [
      { key: 'content', label: 'Zusatzinhalte', children: contentTab },
      { key: 'entries', label: `Teilnehmer (${giveaway.confirmedCount}/${giveaway.entryCount})`, children: <GiveawayEntriesTab giveawayId={giveaway.id} /> },
    ] : []),
  ];

  return (
    <div>
      {modalContext}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/giveaways')}>Zurück</Button>
          <Title level={3} style={{ margin: 0 }}>{isNew ? 'Neues Gewinnspiel' : giveaway?.title}</Title>
          {status && <Tag color={status.color}>{status.label}</Tag>}
        </Space>
        <Space wrap>
          {giveaway && (
            <Button icon={<EyeOutlined />} href={previewUrl} target="_blank" data-testid="giveaway-preview">Vorschau</Button>
          )}
          {giveaway && giveaway.entryCount === 0 && (
            <Button danger icon={<DeleteOutlined />} onClick={confirmDelete}>Löschen</Button>
          )}
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={submit} data-testid="giveaway-save">Speichern</Button>
        </Space>
      </div>

      <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
        <Tabs activeKey={activeTab} onChange={(key) => setSearchParams({ tab: key }, { replace: true })} items={tabs} />
      </Form>
    </div>
  );
};

export default GiveawayEditorPage;
